import { useMemo, type ReactNode } from 'react'
import { SubmitTicketView as SdkSubmitTicketView } from '@reqdesk/sdk-react'
import type { TicketResult, WidgetError } from '../types'

/**
 * Pre-attached file produced by host integrations before the form
 * mounts (extension auto-screenshot, recorded clip). The widget
 * historically passed these as `File` objects + `dataUrl`. The sdk-react
 * canonical accepts dataURL strings directly, so we forward
 * `dataUrl` and ignore the redundant File handle.
 */
export interface PreAttachedFile {
  file?: File
  dataUrl?: string
  source?: 'screenshot' | 'recording' | 'manual' | string
}

export interface PreFilledMetadata {
  url?: string
  title?: string
  userAgent?: string
  viewport?: { width: number; height: number }
}

export interface DraftSnapshot {
  title?: string
  description?: string
  email?: string
}

/**
 * Slot seams kept for back-compat with v1.3 hosts. The canonical
 * sdk-react view does not currently render these slots; passing
 * them is silently a no-op until the canonical adds the seams.
 * Hosts can stop passing slots without behaviour change.
 */
export interface SubmitTicketViewSlots {
  aboveForm?: ReactNode
  diagnostics?: ReactNode
  aboveAttachments?: ReactNode
  belowSubmit?: ReactNode
}

interface SubmitTicketViewProps {
  /** Kept on the API surface for back-compat — sdk-react resolves the
   *  projectId from `<ReqdeskProvider>` context, so this prop is
   *  effectively ignored. The widget's ReqdeskProvider already
   *  configures the same projectId on both contexts via SdkReactBridge. */
  projectId?: string
  onSuccess?: (result: TicketResult) => void
  onError?: (error: WidgetError) => void
  isAuthenticated?: boolean
  userEmail?: string
  slots?: SubmitTicketViewSlots
  preAttachedFiles?: PreAttachedFile[]
  preFilledMetadata?: PreFilledMetadata
  onDraftChange?: (snapshot: DraftSnapshot) => void
}

/**
 * Re-export of `@reqdesk/sdk-react` canonical view, with a thin shim
 * that translates the widget's historical prop names into the
 * canonical's API. Pre-attached File handles get unwrapped into
 * dataURL strings; slot props are preserved on the type but currently
 * pass through as a no-op (the canonical's slot wiring lives one
 * commit ahead).
 */
export function SubmitTicketView({
  projectId: _projectId,
  onSuccess,
  onError,
  isAuthenticated,
  userEmail,
  slots: _slots,
  preAttachedFiles,
  preFilledMetadata: _preFilledMetadata,
  onDraftChange,
}: SubmitTicketViewProps) {
  const attachments = useMemo(() => {
    if (!preAttachedFiles?.length) return undefined
    return preAttachedFiles
      .map(p => p.dataUrl)
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
  }, [preAttachedFiles])

  return (
    <SdkSubmitTicketView
      onSuccess={onSuccess as (result: import('@reqdesk/sdk-core').TicketResult) => void | undefined}
      onError={onError as (error: import('@reqdesk/sdk-core').WidgetError) => void | undefined}
      isAuthenticated={isAuthenticated}
      userEmail={userEmail}
      attachments={attachments}
      onDraftChange={onDraftChange}
    />
  )
}
